import { type FunctionComponent, type ReactNode, type ForwardedRef, type JSX } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
// local imports
import { type Identified } from '../models/item';
import { type Values } from '../helpers/intl';
import { simpleForwardRef, type ReactComponent } from '../helpers/react';
import BoxTypography from '../mixins/BoxTypography';
import FetchFailedAlert from '../elements/FetchFailedAlert';
import LoadingPlaceholder from '../elements/LoadingPlaceholder';
import { hasPaginationElements } from '../elements/Pagination';
import CardDeck from '../elements/CardDeck';
import CardSection from '../elements/CardSection';
import ReloadingOverlay from '../elements/ReloadingOverlay';
// SCSS imports
import { topPadding, bottomPadding, shadyBg } from '../styles/modules/ItemsGrid.module.scss';

export interface GridFiltersProps {
  readonly visible: boolean;
}
export type GridFiltersComponent = ReactComponent<GridFiltersProps>;

export const hasFilters = (filters?: GridFiltersComponent | null) => {
  if (!filters) return false;
  if (!filters.type?.displayName?.endsWith('Filters')) return true;
  return Boolean(filters.props?.visible);
};

export interface GridPaginationProps {
  readonly loaded: boolean;
  readonly pending?: boolean | null;
  readonly total?: number | null;
  readonly totalOverride?: ReactNode;
}
export type PaginationControlsComponent = ReactComponent<GridPaginationProps>;

export const PAGINATION_CONTROLS_DISPLAY_NAME = 'PaginationControls' as const;

export const hasPagination = (pagination?: PaginationControlsComponent | null) => {
  if (!pagination) return false;
  if (!pagination.type?.displayName?.endsWith(PAGINATION_CONTROLS_DISPLAY_NAME)) return true;
  const { loaded, total, pending, totalOverride } = pagination.props || {};
  return hasPaginationElements(loaded, total, pending) || Boolean(totalOverride);
};

export const CARDS_GRID_DARK = 'dark' as const;
export const CARDS_GRID_SHADY = 'shady' as const;
export const CARDS_GRID_WHITE = 'white' as const;
type CardsGridVariant = typeof CARDS_GRID_DARK | typeof CARDS_GRID_SHADY | typeof CARDS_GRID_WHITE;

// Generic React Component approach from:
// https://medium.com/weekly-webtips/typescript-generic-react-components-17c71a64150e
// https://github.com/janjakubnanista/generic-select-react-component

type CardsGridProps<T, P, C> = {
  readonly items?: T[] | null;
  readonly title?: ReactNode;
  readonly values?: Values;
  readonly variant?: CardsGridVariant;
  readonly withReloading?: boolean;
  readonly withoutTopPadding?: boolean | null;
  readonly notFoundMessage?: ReactNode | string | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly blendNotFound?: boolean;
  readonly blendFilters?: boolean;
  readonly blendPagination?: boolean;
  readonly filters?: ReactNode;
  readonly pagination?: ReactNode;
  readonly disabled?: boolean | null;
  readonly removingId?: number | null;
  readonly itemsPrefix?: ReactNode;
  readonly itemsSuffix?: ReactNode;
  readonly sx?: SxProps<Theme>;
  readonly ComponentProps?: Partial<P>;
  readonly component: C;
};

const CardsGridRender = <
  T extends Identified,
  P extends {
    readonly item: T;
    readonly disabled?: boolean | null;
    readonly pending?: boolean | null;
  },
  C extends FunctionComponent<P>
>({
  items,
  title,
  values,
  variant = CARDS_GRID_DARK,
  pending = false,
  failed = false,
  blendNotFound = false,
  blendFilters = false,
  blendPagination = false,
  filters,
  pagination,
  withReloading = false,
  withoutTopPadding = false,
  notFoundMessage,
  disabled = false,
  removingId,
  itemsPrefix,
  itemsSuffix,
  sx,
  ComponentProps = {} as Partial<P>,
  component: Component
  // eslint-disable-next-line complexity
}: CardsGridProps<T, P, C>, ref: ForwardedRef<HTMLDivElement>) => {
  const withFilters = hasFilters(filters as GridFiltersComponent);
  const withPagination = hasPagination(pagination as PaginationControlsComponent);
  const loading = withReloading ? pending && !items : pending || !items;
  const reloading = Boolean(withReloading && pending && items);
  const filtersContent = withFilters ? (
    <CardSection
        top
        flex
        narrow
        dark={blendFilters && variant === CARDS_GRID_DARK ? true : undefined}
        light={blendFilters && variant === CARDS_GRID_SHADY ? true : undefined}
    >
      {filters}
    </CardSection>
  ) : filters;
  const content = (
    <>
      {failed || loading || (size(items) < 1 && !itemsPrefix && !itemsSuffix) ? (
        <Box
            ref={ref}
            bgcolor={variant === CARDS_GRID_DARK &&
              ((withPagination && blendPagination && loading) || (withFilters && blendFilters && (loading || failed)))
              ? 'background.card' : undefined}
            className={clsx({
              [topPadding]: withFilters && (
                failed || (loading && (blendFilters || variant !== CARDS_GRID_SHADY)) || !withoutTopPadding
              ),
              [bottomPadding]: withPagination && failed && variant === CARDS_GRID_WHITE,
              [shadyBg]: variant === CARDS_GRID_SHADY &&
                ((withPagination && loading) || (withFilters && blendFilters && (loading || failed)))
            })}
            sx={sx}
        >
          {(failed && <FetchFailedAlert flat/>) ||
          (loading && <LoadingPlaceholder flat/>) ||
          (!isNil(notFoundMessage) && !isString(notFoundMessage) && notFoundMessage) ||
          (blendNotFound && variant === CARDS_GRID_SHADY && (
            <CardSection shady>
              <BoxTypography variant="body1" color="info.caption">
                <FormattedMessage id={(isString(notFoundMessage) && notFoundMessage) || 'common.empty'}/>
              </BoxTypography>
            </CardSection>
          )) || (
            <>
              {withFilters && !blendFilters && !blendNotFound ? <Divider/> : undefined}
              <Box
                  bgcolor={variant === CARDS_GRID_DARK ? 'background.card' : undefined}
                  className={variant === CARDS_GRID_SHADY ? shadyBg : undefined}
              >
                <Alert severity="info" variant="standard">
                  <FormattedMessage
                      id={(isString(notFoundMessage) && notFoundMessage) || 'common.empty'}
                      values={values}
                  />
                </Alert>
              </Box>
            </>
          )}
        </Box>
      ) : (
        <CardDeck
            ref={ref}
            title={isString(title) && size(title) >= 1 ? (
              <Typography variant="subtitle1">
                <FormattedMessage id={title} values={values} defaultMessage={title}/>
              </Typography>
            ) : title}
            dark={variant === CARDS_GRID_DARK}
            shady={variant === CARDS_GRID_SHADY}
            sx={sx}
        >
          {itemsPrefix}
          {map(items, (item) => (
            <Component
                key={item.id}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...{
                  ...ComponentProps,
                  item,
                  disabled,
                  ...removingId && removingId === item.id ? { pending: true } : {}
                } as unknown as JSX.IntrinsicAttributes & JSX.LibraryManagedAttributes<C, P>}
            />
          ))}
          {itemsSuffix}
        </CardDeck>
      )}
      {withPagination ? (
        <CardSection
            flex
            narrow
            bottom={!loading && (blendPagination || variant === CARDS_GRID_WHITE)}
            dark={blendPagination && variant !== CARDS_GRID_WHITE ? true : undefined}
        >
          {pagination}
        </CardSection>
      ) : pagination}
    </>
  );
  return (
    <>
      {blendFilters ? undefined : filtersContent}
      <ReloadingOverlay reloading={reloading}>
        {blendFilters ? filtersContent : undefined}
        {content}
      </ReloadingOverlay>
    </>
  );
};

const CardsGrid = simpleForwardRef(CardsGridRender);

// CardsGrid.displayName = 'CardsGrid';

export default CardsGrid;
